import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Sortable from "sortablejs";
import { validateFiles } from "./file_validation";

export default class extends Controller {
  static targets = [
    "file",
    "list",
    "wrapper",
    "generalError",
    "completeButton",
    "pendingTemplate",
    "errorTemplate",
    "totalCounter",
    "totalCounterText",
  ];

  static values = {
    saveOnUpload: { type: Boolean, default: false },
    reorder: { type: Boolean, default: false },
    reorderFieldName: { type: String, default: "" },
    showTotal: { type: Boolean, default: false },
    totalLabel: { type: String, default: "Files" },
    recordType: { type: String, default: "" },
    recordId: { type: String, default: "" },
    fieldName: { type: String, default: "" },
    maxFiles: { type: Number, default: 15 },
    maxTotalSize: { type: Number, default: 0 },
    maxFileSize: { type: Number, default: 15728640 },
    minWidth: { type: Number, default: 0 },
    minHeight: { type: Number, default: 0 },
    acceptedFormats: { type: Array, default: [] },
  };

  connect() {
    this.pendingUploads = 0;
    this.initDragAndDrop();
    if (this.reorderValue) this.initSortable();
    this.onStreamRender = this.handleStreamRender.bind(this);
    document.addEventListener("turbo:before-stream-render", this.onStreamRender);
    this.updatePositionBadges();
    this.updateTotalCounter();
    this.updateButtonState();
  }

  disconnect() {
    this.sortable?.destroy();
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender);
  }

  handleStreamRender(event) {
    const originalRender = event.detail.render;
    event.detail.render = (streamElement) => {
      const target = document.getElementById(streamElement.target);
      const affectsOurList =
        target && (this.listTarget.contains(target) || target === this.listTarget);
      originalRender(streamElement);
      if (affectsOurList) {
        this.updatePositionBadges();
        this.updateTotalCounter();
        this.updateButtonState();
        if (this.reorderValue) this.handleSort();
      }
    };
  }

  initDragAndDrop() {
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    ["dragenter", "dragover", "dragleave", "drop"].forEach((event) => {
      this.wrapperTarget.addEventListener(event, prevent);
    });

    ["dragenter", "dragover"].forEach((event) => {
      this.wrapperTarget.addEventListener(event, () =>
        this.wrapperTarget.classList.add("dragover"),
      );
    });

    ["dragleave", "drop"].forEach((event) => {
      this.wrapperTarget.addEventListener(event, () =>
        this.wrapperTarget.classList.remove("dragover"),
      );
    });

    this.wrapperTarget.addEventListener("drop", this.handleDrop.bind(this));
  }

  initSortable() {
    this.sortable = new Sortable(this.listTarget, {
      animation: 150,
      ghostClass: "opacity-50",
      handle: ".file-upload-item",
      onEnd: this.handleSort.bind(this),
    });
  }

  async handleDrop(event) {
    const files = Array.from(event.dataTransfer.files);
    await this.processFiles(files);
  }

  async updateFileList() {
    const files = Array.from(this.fileTarget.files);
    await this.processFiles(files);
    this.fileTarget.value = "";
  }

  async processFiles(files) {
    if (files.length === 0) return;

    const existingCount = this.listTarget.querySelectorAll(".file-upload-item").length;

    // For maxFiles: 1, clear old items before uploading the new one
    if (this.maxFilesValue === 1 && existingCount > 0) {
      this.clearUnsavedItems();
    }

    const effectiveMaxFiles = Math.max(1, this.maxFilesValue - existingCount);

    const { fileErrors, generalError } = await validateFiles(
      files,
      effectiveMaxFiles,
      this.maxTotalSizeValue || null,
      this.maxFileSizeValue,
      this.acceptedFormatsValue,
      this.minWidthValue || null,
      this.minHeightValue || null,
    );

    if (generalError) {
      this.showGeneralError(generalError);
      return;
    }

    this.clearGeneralError();
    this.listTarget.classList.remove("hidden");

    const validFiles = files.filter((file) => !fileErrors.find((e) => e.file === file));
    validFiles.forEach((file) => this.uploadFile(file));

    fileErrors.forEach(({ file, reason }) => this.appendErrorItem(file, reason));
  }

  clearUnsavedItems() {
    this.listTarget.querySelectorAll(".file-upload-item").forEach((item) => {
      if (!item.dataset.attachmentId) item.remove();
    });
  }

  uploadFile(file) {
    const pendingId = `pending-upload-${this.generateId()}`;
    this.appendPendingItem(pendingId);
    this.pendingUploads++;
    this.updateButtonState();

    const url = `/rails/active_storage/direct_uploads?attachment=${encodeURIComponent(
      this.fileTarget.name,
    )}`;
    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return;
          const percent = Math.round((event.loaded / event.total) * 100);
          const bar = document.getElementById(pendingId)?.querySelector(".upload-progress-bar");
          if (bar) bar.style.width = `${percent}%`;
        });
      },
    });

    upload.create((error, blob) => {
      this.pendingUploads--;

      if (error) {
        const el = document.getElementById(pendingId);
        if (el) el.replaceWith(this.buildErrorItem(`error-${pendingId}`, error));
        this.updateButtonState();
        return;
      }

      this.finalizeUpload(pendingId, blob);
    });
  }

  // Many of these can happen in parallel, so we need to do a fetch here.
  // Turbo would cancel the previous request if a new one is made, which would cause some uploads to be lost.
  async finalizeUpload(pendingId, blob) {
    const body = {
      signed_id: blob.signed_id,
      pending_id: pendingId,
      field_name: this.fileTarget.name,
    };

    if (this.saveOnUploadValue) {
      body.record_type = this.recordTypeValue;
      body.record_id = this.recordIdValue;
      body.field_name = this.fieldNameValue;
    }

    const response = await fetch("/file_uploads", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const el = document.getElementById(pendingId);
      if (el)
        el.replaceWith(
          this.buildErrorItem(`error-${pendingId}`, "Upload failed. Please try again."),
        );
      this.updateButtonState();
      return;
    }

    const html = await response.text();
    window.Turbo.renderStreamMessage(html);
  }

  // Unsaved files only; saved files are removed by a Turbo delete link in the item partial.
  removeFile(event) {
    event.currentTarget.closest(".file-upload-item")?.remove();
    this.updateButtonState();
    this.updateTotalCounter();

    if (this.listTarget.querySelectorAll(".file-upload-item").length === 0) {
      this.listTarget.classList.add("hidden");
    }
  }

  // This is here just for the tributes memory, Need to remove this after we refactor tributes memories to use turbo streams
  removeAllFiles() {
    const items = Array.from(this.listTarget.querySelectorAll(".file-upload-item"));
    items.forEach((item) => item.remove());
    this.updateButtonState();
    this.updateTotalCounter();
    this.listTarget.classList.add("hidden");
  }

  // A delete can happen in parallel with other uploads, so we need to do a fetch here.
  handleSort() {
    this.updatePositionBadges();
    if (!this.reorderFieldNameValue) return;

    const order = Array.from(this.listTarget.querySelectorAll(":scope > [data-attachment-id]"))
      .map((el) => el.dataset.attachmentId)
      .filter(Boolean);

    fetch("/file_uploads/reorder", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: JSON.stringify({
        record_type: this.recordTypeValue,
        record_id: this.recordIdValue,
        field_name: this.reorderFieldNameValue,
        order,
      }),
    });
  }

  appendPendingItem(pendingId) {
    const clone = this.pendingTemplateTarget.content.cloneNode(true);
    clone.firstElementChild.id = pendingId;
    this.listTarget.appendChild(clone);
  }

  buildErrorItem(id, reason) {
    const clone = this.errorTemplateTarget.content.cloneNode(true);
    const el = clone.firstElementChild;
    el.id = id;
    el.title = reason;
    const hoverLabel = el.querySelector(".error-hover-label");
    if (hoverLabel) hoverLabel.textContent = reason;
    return el;
  }

  appendErrorItem(file, reason) {
    const el = this.buildErrorItem(`error-item-${this.generateId()}`, reason);
    this.listTarget.appendChild(el);
  }

  dismissError(event) {
    event.currentTarget.closest(".file-upload-item").remove();
  }

  showGeneralError(message) {
    this.wrapperTarget.classList.add("error");
    this.generalErrorTarget.textContent = message;
    this.generalErrorTarget.classList.remove("hidden");
  }

  clearGeneralError() {
    this.wrapperTarget.classList.remove("error");
    this.generalErrorTarget.classList.add("hidden");
  }

  successfulItems() {
    return Array.from(
      this.listTarget.querySelectorAll(
        ".file-upload-item[data-signed-id], .file-upload-item[data-attachment-id]",
      ),
    );
  }

  updateButtonState() {
    const hasItems = this.successfulItems().length > 0;
    if (this.hasCompleteButtonTarget) {
      this.completeButtonTarget.disabled = this.pendingUploads > 0 || !hasItems;
    }
  }

  updatePositionBadges() {
    if (!this.reorderValue) return;
    this.successfulItems().forEach((item, index) => {
      const badge = item.querySelector(".position-badge");
      if (!badge) return;
      badge.textContent = `${index + 1} of ${this.maxFilesValue}`;
      badge.classList.remove("hidden");
    });
  }

  updateTotalCounter() {
    if (!this.hasTotalCounterTarget) return;

    const count = this.successfulItems().length;

    if (!count) {
      this.totalCounterTextTarget.textContent = "";
      this.totalCounterTarget.classList.add("hidden");
      return;
    }

    this.totalCounterTarget.classList.remove("hidden");

    if (this.showTotalValue) {
      this.totalCounterTextTarget.textContent = `${this.totalLabelValue}: ${count}/${this.maxFilesValue}`;
    } else {
      this.totalCounterTextTarget.textContent = `${this.totalLabelValue}: ${count}`;
    }
  }

  generateId() {
    return Math.random().toString(36).slice(2, 10);
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ?? "";
  }
}
